'use strict';

/**
 * PictView-Checklists
 * -------------------
 * A collection of checklists for one context, rendered as a stack of pict-section-checklist controls.
 * Two modes, never on screen at the same time, so one registered instance serves both:
 *   - 'mine'  : the signed-in user's PRIVATE checklists (the personal "Checklists" page).
 *   - 'owner' : the SHARED checklists on an entity (a Work Item's, blueprint-visible).
 *
 * Each checklist row gets its own mount div and its own child checklist view (PSChecklist-<id>), bound
 * to that checklist by Context ({ OwnerType: 'Checklist', IDOwner: <id> }) and backed by the shared
 * PlansheetChecklistProvider. The collection only manages the list itself (load, create, delete); every
 * item edit flows through the child control and the provider.
 */

const libPictView = require('pict-view');
const libModal = require('pict-section-modal');
const libChecklist = require('pict-section-checklist');
const libPlansheetChecklistProvider = require('../providers/PlansheetChecklistProvider.js');

const _DefaultConfiguration =
{
	ViewIdentifier: 'Plansheet-Checklists',
	DefaultRenderable: 'Plansheet-Checklists',
	DefaultDestinationAddress: '#Plansheet-Checklists-Container',
	AutoRender: false,

	Mode: 'mine',            // 'mine' | 'owner'
	OwnerType: '',           // for 'owner' mode
	IDOwner: 0,              // for 'owner' mode
	Heading: 'Checklists',
	NewLabel: 'New checklist',
	Compact: false,          // true drops the card chrome for the narrow rail / phone column

	CSSPriority: 510,
	CSS: /*css*/`
		/* root container styling comes from .ps-page (Shell) */
		.ps-checklists-head { display: flex; align-items: center; gap: 12px; margin: 0 0 16px; }
		.ps-checklists-head h2 { font-size: 22px; font-weight: 600; margin: 0; color: var(--theme-color-text-primary, #1f2430); }
		.ps-checklists-new { font: inherit; font-size: 13px; margin-left: auto; border: 1px solid var(--theme-color-brand-primary, #2880a6); background: var(--theme-color-brand-primary, #2880a6); color: var(--theme-color-text-on-brand, #fff); border-radius: 7px; padding: 6px 13px; cursor: pointer; }
		.ps-checklists-new:hover { filter: brightness(1.06); }
		.ps-checklist-card { position: relative; background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 12px; padding: 16px 18px; margin-bottom: 14px; }
		.ps-checklist-del { position: absolute; top: 10px; right: 10px; z-index: 2; border: none; background: none; color: var(--theme-color-text-muted, #97a1ab); cursor: pointer; font-size: 15px; line-height: 1; padding: 4px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
		.ps-checklist-del:hover { background: var(--theme-color-background-hover, #eef2f6); color: var(--theme-color-status-error, #c0392b); }
		.ps-checklist-mount { }
		.ps-checklist-card .pchk-header { padding-right: 30px; }
		.ps-checklists-empty { color: var(--theme-color-text-muted, #97a1ab); font-size: 14px; padding: 14px 2px; }

		/* Compact mode (the notebook rail / a phone-width column): drop the card chrome so lists use the
		   full narrow width -- no border, no heavy padding, tighter heading. The child pict-section-checklist
		   controls handle their own narrow-width layout via container queries. */
		.ps-checklists-compact.ps-page { padding: 0; }
		.ps-checklists-compact .ps-checklists-head { margin-bottom: 10px; }
		.ps-checklists-compact .ps-checklists-head h2 { font-size: 15px; }
		.ps-checklists-compact .ps-checklists-new { padding: 4px 10px; font-size: 12px; }
		.ps-checklists-compact .ps-checklist-card { border: none; background: transparent; border-radius: 0; padding: 0; margin-bottom: 18px; }
		.ps-checklists-compact .ps-checklist-card .pchk-header { padding-right: 26px; }
	`,

	Templates:
	[
		{
			Hash: 'Plansheet-Checklists',
			Template: /*html*/`
<div class="{~D:AppData.ChecklistsPage.RootClass~}" id="Plansheet-Checklists-Root">
	<div class="ps-checklists-head">
		<h2>{~D:AppData.ChecklistsPage.Heading~}</h2>
		<button class="ps-checklists-new" onclick="_Pict.views['{~D:AppData.ChecklistsPage.ViewHash~}'].createChecklist()">{~D:AppData.ChecklistsPage.NewLabel~}</button>
	</div>
	{~TS:Plansheet-Checklist-Card:AppData.ChecklistsPage.Rows~}
	{~TS:Plansheet-Checklists-Empty:AppData.ChecklistsPage.EmptySlot~}
</div>`
		},
		{
			Hash: 'Plansheet-Checklist-Card',
			Template: /*html*/`<div class="ps-checklist-card"><button class="ps-checklist-del" title="Delete checklist" onclick="_Pict.views['{~D:Record.ViewHash~}'].deleteChecklist('{~D:Record.IDChecklist~}')">{~I:Trash~}</button><div class="ps-checklist-mount" id="{~D:Record.MountId~}"></div></div>`
		},
		{
			Hash: 'Plansheet-Checklists-Empty',
			Template: /*html*/`<div class="ps-checklists-empty">{~D:Record.Message~}</div>`
		}
	],

	Renderables:
	[
		{
			RenderableHash: 'Plansheet-Checklists',
			TemplateHash: 'Plansheet-Checklists',
			ContentDestinationAddress: '#Plansheet-Checklists-Container',
			RenderMethod: 'replace'
		}
	]
};

class PictViewChecklists extends libPictView
{
	onBeforeInitialize()
	{
		this._rows = [];
		this._provider = new libPlansheetChecklistProvider(this.pict);
		this._childHashes = {};
		return super.onBeforeInitialize();
	}

	// Render the page (used by the route). Options (Mode/OwnerType/IDOwner) are set at registration or
	// via configure(); this just loads + paints into the destination.
	show() { return this.reload(); }

	// Point the collection at an owner (or 'mine') and reload. Call before rendering.
	configure(pMode, pOwnerType, pIDOwner)
	{
		this.options.Mode = pMode || 'mine';
		this.options.OwnerType = pOwnerType || '';
		this.options.IDOwner = pIDOwner || 0;
		return this.reload();
	}

	reload()
	{
		this._ensureModal();
		let tmpAPI = this.pict.providers.ChecklistAPI;
		let tmpLoad = (this.options.Mode === 'owner')
			? tmpAPI.listForOwner(this.options.OwnerType, this.options.IDOwner)
			: tmpAPI.listMine();
		return tmpLoad.then((pRows) =>
		{
			this._rows = Array.isArray(pRows) ? pRows : [];
			this._shape();
			this.render();
		}).catch(() =>
		{
			this._rows = [];
			this._shape();
			this.render();
		});
	}

	_shape()
	{
		this.pict.AppData.ChecklistsPage =
		{
			ViewHash: this.Hash,
			RootClass: 'ps-page ps-checklists' + (this.options.Compact ? ' ps-checklists-compact' : ''),
			Heading: this.options.Heading,
			NewLabel: this.options.NewLabel,
			Rows: this._rows.map((pRow) => ({ ViewHash: this.Hash, IDChecklist: pRow.IDChecklist, MountId: 'pschecklist-mount-' + pRow.IDChecklist })),
			EmptySlot: (this._rows.length === 0) ? [{ Message: (this.options.Mode === 'owner') ? 'No checklists here yet. Add one to track the work.' : 'No personal checklists yet. These are private to you.' }] : []
		};
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._mountChildren();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_mountChildren()
	{
		for (let i = 0; i < this._rows.length; i++)
		{
			let tmpID = this._rows[i].IDChecklist;
			let tmpHash = 'PSChecklist-' + tmpID;
			let tmpMount = '#pschecklist-mount-' + tmpID;
			let tmpView = this.pict.views[tmpHash];
			if (!tmpView)
			{
				tmpView = this.pict.addView(tmpHash, Object.assign({}, libChecklist.default_configuration,
					{
						ViewIdentifier: tmpHash,
						AutoRender: false,
						Context: { OwnerType: 'Checklist', IDOwner: String(tmpID) },
						DataProvider: this._provider,
						Title: this._rows[i].Title || 'Checklist',
						EditableTitle: true,
						DefaultDestinationAddress: tmpMount,
						Renderables: [{ RenderableHash: 'Checklist-Section', TemplateHash: 'Checklist-Section', ContentDestinationAddress: tmpMount, RenderMethod: 'replace' }]
					}), libChecklist);
			}
			if (!this._childHashes[tmpHash])
			{
				// pict's addView after app init does NOT run the view lifecycle, so bring the control up
				// by hand: the sync stages via initialize(), then mount() (wires the provider, loads, paints).
				this._childHashes[tmpHash] = true;
				if (typeof tmpView.initialize === 'function' && !tmpView.initializeTimestamp) { tmpView.initialize(); }
				if (typeof tmpView.mount === 'function') { try { tmpView.mount(); } catch (pError) { /* ignore */ } }
				else { try { tmpView.render(); } catch (pError) { /* ignore */ } }
			}
			else
			{
				// The shell re-rendered the mount div; repaint the existing control into the fresh node.
				try { tmpView.render(); } catch (pError) { /* ignore */ }
			}
		}
	}

	createChecklist()
	{
		let tmpDraft = (this.options.Mode === 'owner')
			? { OwnerType: this.options.OwnerType, IDOwner: this.options.IDOwner, Title: 'Checklist' }
			: { Private: true, Title: 'My checklist' };
		return this.pict.providers.ChecklistAPI.create(tmpDraft).then(() => this.reload());
	}

	deleteChecklist(pID)
	{
		return this._confirm('Delete this checklist and all of its items?', { title: 'Delete checklist', confirmLabel: 'Delete', dangerous: true })
			.then((pOk) =>
			{
				if (!pOk) { return; }
				let tmpHash = 'PSChecklist-' + pID;
				if (this.pict.views[tmpHash]) { try { delete this.pict.views[tmpHash]; } catch (pError) { /* ignore */ } delete this._childHashes[tmpHash]; }
				return this.pict.providers.ChecklistAPI.remove(pID).then(() => this.reload());
			});
	}

	_ensureModal()
	{
		if (!this.pict.views['Pict-Section-Modal']) { this.pict.addView('Pict-Section-Modal', libModal.default_configuration, libModal); }
		let tmpModalView = this.pict.views['Pict-Section-Modal'];
		if (tmpModalView && typeof document !== 'undefined' && document.body && typeof tmpModalView.initialize === 'function') { tmpModalView.initialize(); }
	}

	_confirm(pMessage, pOptions)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.confirm === 'function') { return tmpModal.confirm(pMessage, pOptions || {}); }
		return Promise.resolve(true);
	}
}

module.exports = PictViewChecklists;
module.exports.default_configuration = _DefaultConfiguration;
